import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, TextField, Typography, alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import type { AxiosError } from 'axios';
import { api } from '../api/client';

const GREEN = '#16c784';
const RED = '#ea3943';

interface Trade {
  pendingId: number; clientOrderId: string; symbol: string; side: string; market: string;
  orderType: string; quantity: string; price: string | null; leverage: number | null;
  clientName: string; provider: string; accountLabel: string | null; requiredApprovals: number;
}
interface ViewResp {
  valid: boolean; reason?: string; usedAction?: string; status?: string;
  adminName?: string; trade?: Trade;
}

const fmtNum = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  if (!s.includes('.')) return s;
  const t = s.replace(/0+$/, '').replace(/\.$/, '');
  return t === '' ? '0' : t;
};

const INVALID_MSG: Record<string, string> = {
  INVALID: 'This link is not valid. It may have been mistyped or revoked.',
  USED: 'This link has already been used. The decision was already recorded.',
  EXPIRED: 'This link has expired. Please log in to review the trade.',
  RESOLVED: 'This trade has already been resolved and is no longer awaiting approval.',
};

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{value}</Typography>
    </Box>
  );
}

export default function TradeActionPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const [view, setView] = useState<ViewResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(params.get('action') === 'reject');
  const [submitting, setSubmitting] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = (await api.get(`/approval-actions/${token}`)).data.data as ViewResp;
        if (active) setView(data);
      } catch {
        if (active) setView({ valid: false, reason: 'INVALID' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (action: 'APPROVE' | 'REJECT') => {
    setSubmitting(action);
    setResult(null);
    try {
      const data = (await api.post(`/approval-actions/${token}`, { action, reason: action === 'REJECT' ? reason : undefined })).data.data;
      if (action === 'APPROVE') {
        const msg = data.status === 'SUBMITTING'
          ? 'Trade approved — all required approvals received. It has been sent for execution.'
          : `Approval recorded (${data.approved}/${data.required}). Awaiting ${Math.max((data.required ?? 0) - (data.approved ?? 0), 0)} more approval(s).`;
        setResult({ ok: true, message: msg });
      } else {
        setResult({ ok: true, message: 'Trade rejected. The client has been notified.' });
      }
    } catch (e) {
      const message = (e as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message || 'Could not complete the action. The link may have expired or already been used.';
      setResult({ ok: false, message });
    } finally {
      setSubmitting(null);
    }
  };

  const shell = (children: ReactNode) => (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, background: '#0b1020' }}>
      <Card sx={{ width: '100%', maxWidth: 460 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Typography sx={{ color: '#4f8cff', fontWeight: 700, fontSize: 20, mb: 2 }}>Blueaster Capital</Typography>
          {children}
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) return shell(<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>);

  // Terminal outcome after acting.
  if (result) {
    return shell(
      <Box sx={{ textAlign: 'center', py: 1 }}>
        {result.ok
          ? <CheckCircleIcon sx={{ fontSize: 56, color: GREEN }} />
          : <CancelIcon sx={{ fontSize: 56, color: RED }} />}
        <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>{result.ok ? 'Done' : 'Unable to complete'}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{result.message}</Typography>
      </Box>,
    );
  }

  // Invalid / used / expired / resolved link.
  if (!view?.valid || !view.trade) {
    return shell(
      <Alert severity={view?.reason === 'USED' ? 'info' : 'warning'}>
        {INVALID_MSG[view?.reason ?? 'INVALID'] ?? INVALID_MSG.INVALID}
      </Alert>,
    );
  }

  const t = view.trade;
  const buy = t.side === 'BUY';
  return shell(
    <>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Trade approval</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {view.adminName ? `Signed in as ${view.adminName}. ` : ''}Review and decide — no login needed.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 22 }}>{t.symbol}</Typography>
        <Chip size="small" label={t.side}
          sx={{ color: buy ? GREEN : RED, fontWeight: 700, backgroundColor: alpha(buy ? GREEN : RED, 0.14), border: `1px solid ${alpha(buy ? GREEN : RED, 0.35)}` }} />
        <Chip size="small" label={t.market === 'FUTURES' ? 'PERP' : t.market} sx={{ color: '#9b8cff', backgroundColor: alpha('#9b8cff', 0.14) }} />
      </Box>

      <Box sx={{ backgroundColor: '#0f1424', borderRadius: 2, px: 2, py: 1, mb: 2, border: '1px solid #243049' }}>
        <Row label="Client" value={t.clientName} />
        <Row label="Account" value={[t.provider, t.accountLabel].filter(Boolean).join(' — ') || '—'} />
        <Row label="Order Type" value={t.orderType} />
        <Row label="Quantity" value={fmtNum(t.quantity)} />
        <Row label="Price" value={t.price != null ? fmtNum(t.price) : (t.orderType.includes('MARKET') ? 'Market' : '—')} />
        {t.leverage ? <Row label="Leverage" value={`${t.leverage}x`} /> : null}
        <Row label="Request ID" value={`${t.clientOrderId} (#${t.pendingId})`} />
        <Row label="Approvals Required" value={`${t.requiredApprovals} admin(s)`} />
      </Box>

      {showReject && (
        <TextField
          fullWidth size="small" label="Rejection reason (optional)" value={reason}
          onChange={(e) => setReason(e.target.value)} multiline minRows={2} sx={{ mb: 2 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          fullWidth variant="contained" color="success" disabled={!!submitting}
          startIcon={submitting === 'APPROVE' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          onClick={() => submit('APPROVE')}
        >
          Approve
        </Button>
        <Button
          fullWidth variant="outlined" color="error" disabled={!!submitting}
          startIcon={submitting === 'REJECT' ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
          onClick={() => (showReject ? submit('REJECT') : setShowReject(true))}
        >
          {showReject ? 'Confirm Reject' : 'Reject'}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        This is a single-use link. {t.requiredApprovals > 1 ? 'Approving records your approval; the trade executes once all required approvals are in.' : ''}
      </Typography>
    </>,
  );
}
